//Helpers
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { gen2Request } from '../services/gen_2_http_reqs';
import { onUpdateResponseFW } from './firmware_helpers';
import { proSensorAddonSupport, type AddonType } from './addons/addon_support_helpers';
//Types
import type { Device } from '../types/Device';
import type { Request } from '../types/requests/request';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { AlternativeFirmware, AlternativeFirmwareMap } from 'src/types/DeviceStatusG2';

export type AltFirmwareStage = 'stable' | 'beta';
export type AltFirmwareOption = {
	stage: AltFirmwareStage;
	title: string;
	version: string;
};

export const ALT_FIRMWARE_STAGES: AltFirmwareStage[] = ['stable', 'beta'];

export const updateAltFirmware = async (
	device: Device,
	addon: AddonType,
	altFirmware: AlternativeFirmware,
	stage: AltFirmwareStage = 'stable',
) => {
	const app = getAltFirmwareApp(device, addon);
	if (!app) return { isok: false } as Request;
	const buildId = altFirmware?.[stage]?.build_id || altFirmware?.stable?.build_id || '';
	const res: Request = await gen2Request(device, 'Shelly.InstallAlt', { app, stage });
	onUpdateResponseFW(res, device, buildId, buildId);
	return res;
};

export const needsAltFirmware = (
	device: Device,
	currentAddonType: AddonType,
	selectedAddonType: AddonType,
) => {
	if (
		!proSensorAddonSupport(device) ||
		currentAddonType === selectedAddonType ||
		selectedAddonType === ''
	) {
		return false;
	}
	return getAltFirmwareApp(device, selectedAddonType) !== null;
};

export const getAltFirmwareApp = (device: Device, addon: AddonType): string | null => {
	const app = devices_settings.getByRealId(device?.id)?.DeviceInfo?.app || '';
	const PRO_ADDON_SUFFIX = 'ProAddon';
	const isProAddonApp = app.endsWith(PRO_ADDON_SUFFIX);

	if (addon !== 'sensor' && isProAddonApp) {
		return app.substring(0, app.length - PRO_ADDON_SUFFIX.length);
	}
	if (addon === 'sensor' && !isProAddonApp) {
		return app + PRO_ADDON_SUFFIX;
	}
	return null;
};

export const getAltFirmware = async (
	device: Device,
	addon: AddonType,
): Promise<AlternativeFirmware | null> => {
	const fw = getAltFirmwareApp(device, addon);
	const sysAlt = await getAltFirmwareStatus(device);
	return sysAlt?.[fw] || null;
};

const getAltFirmwareStatus = async (device: Device): Promise<AlternativeFirmwareMap | null> => {
	const res: SettingRequest = await gen2Request(device, 'Sys.GetStatus', {});
	const deviceStatus = devices_status.getByRealId(device?.id);
	return res?.data?.alt || res?.['alt'] || deviceStatus?.sys?.alt || null;
};
